// Record card (R-U6, DESIGN.md «Запись»): title, properties, related records and «История». A record is its own page with «назад» and
// crumbs; a screen that lays a card out as its own column (the knowledge base, R-U16) embeds it without them, and a linked card opens
// over the screen in the stack of modals (R-U19, shell/CardStack).

import { useEffect, useRef, useState } from 'react';
import { avatarUrl, peopleFields, recordTitle, recordWords, wordedFields, type FieldDef, type Id, type RecordObj } from '@confdeck/core-shared';
import { PopoverEditor } from '../fields/FieldEditor.tsx';
import { archiveRecords, freshRecords, setField, setOwner } from '../sync/mutations.ts';
import { ArchiveConfirm } from './ArchiveConfirm.tsx';
import { useLinked, useMeta, useRecord, useStatus, useSync, useTypesTick } from '../sync/react.tsx';
import { UserPicker } from '../fields/pickers.tsx';
import { UserValue } from '../fields/FieldValue.tsx';
import { Icon } from '../ui/icons.tsx';
import { recordTabsOf } from '../shell/screens.ts';
import { RecordSettings, recordDecls } from '../settings/RecordSettings.tsx';
import { IconButton, Avatar, Button, MenuItem, MenuSeparator, Popover, PopoverAnchor, PopoverContent, RecordChip, Tabs, cx, useToast } from '../ui/primitives.tsx';
import { History } from './History.tsx';
import { ScopeActivity } from './ScopeActivity.tsx';
import { Letters } from './Letters.tsx';
import { RecordActions } from './RecordActions.tsx';
import { TemplateTestButton } from '../mail/TemplateTestButton.tsx';
import { RunLog } from '../automations/RunLog.tsx';
import { TopBar, TopTitle } from '../ui/TopBar.tsx';
import { rememberRecord } from '../shell/recent.ts';
import { RecordPath } from './RecordPath.tsx';
import { Properties } from './Properties.tsx';
import { LongText } from './LongText.tsx';
import { RelationBlocks } from './RelationBlocks.tsx';
import { useWritable } from './Prop.tsx';
import { singleClass } from '../ui/Page.tsx';
import { stackOpen } from '../ui/ModalStack.tsx';
import s from './record.module.css';

/** A card asked to open on a tab (a first conference opens on its launch checklist); taken once by the card it names. */
const askedTabs = new Map<string, string>();
export function openOnTab(recordId: string, tab: string) { askedTabs.set(recordId, tab); }

/**
 * `embedded` — the card is a column of a screen or a modal: its head is the type and «открыть страницей», not the path.
 * `inStack` — a modal of the stack: Esc belongs to the stack, not to the card.
 */
export function RecordView({ id, onClose, onOpen, embedded, inStack }: { id: Id; onClose: () => void; onOpen: (id: Id) => void; embedded?: { onExpand: () => void }; inStack?: boolean }) {
  const client = useSync();
  const meta = useMeta();
  const rec = useRecord(id);
  const toast = useToast();
  const { presence } = useStatus();
  const [tab, setTab] = useState<string>('props');
  const [archiving, setArchiving] = useState(false);

  useEffect(() => { client.setPresence(id); return () => client.setPresence(null); }, [id, client]);
  useEffect(() => { if (rec) rememberRecord(id); }, [id, !!rec]);
  // Another record (possibly of another type) opens on «Свойства»: an app tab of the previous one may not exist here.
  // Another record opens on its properties, or on the tab it was asked to open on.
  useEffect(() => { const asked = askedTabs.get(id); askedTabs.delete(id); setTab(asked ?? 'props'); }, [id]);
  // Esc on a record just made and never edited drops it: nothing typed, nothing kept (L10).
  const close = () => { if (freshRecords.has(id)) { freshRecords.delete(id); archiveRecords(client, [id]); } onClose(); };
  // The latest close in a ref: the shell gives a new onClose on each render, the key listener stays one per record.
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (inStack) return;
    // A card over this one (the stack) takes Esc: the page under it stays.
    const on = (e: KeyboardEvent) => { if (e.key === 'Escape' && !stackOpen() && !e.defaultPrevented && !(e.target as HTMLElement).closest('input, textarea, [role=dialog], [role=menu], [role=listbox]')) closeRef.current(); };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [id, inStack]);

  if (!rec) {
    return (
      <div className={cx(s.view, !embedded && s.page)}>
        {!embedded && <TopBar start={<RecordPath rec={undefined} onBack={onClose} />} />}
        {/* On a direct visit the record may come with a later page of the snapshot: until it is all in, the record is loading. */}
        <div className={s.gone}>{client.timings.loaded === undefined ? 'Загружается…' : 'Запись не найдена или перенесена в архив.'}{embedded && <Button onClick={onClose}>Закрыть</Button>}</div>
      </div>
    );
  }
  const type = meta.type(rec.typeId);
  const titleField = meta.fields(rec.typeId).find((f) => f.key === type?.titleField);
  // The app's own data (the rules of the schedule builder) is its screen's, not a property of the card.
  // R-E9: the record's words (TypeDef.wording) hide fields its kind has not and rename others; the data stays.
  const words = recordWords(meta, rec, client.store);
  const fields = wordedFields(peopleFields(meta.fields(rec.typeId)), words);
  const props = fields.filter((f) => f.key !== titleField?.key && f.fieldType !== 'relation' && f.fieldType !== 'longtext');
  // A record that holds its own values of settings (R-E7: a project, an event) gets the tab «Настройки» after the app's tabs.
  const own = recordTabsOf(type?.key).filter((t) => !t.field || [t.field].flat().some((k) => meta.field(rec.typeId, k) && !words.hidden.has(k)));
  const heads = own.filter((t) => t.head);
  const appTabs = [...own.filter((t) => !t.head),
    ...(recordDecls(client.header?.settings, rec.typeId).length ? [{ key: 'settings', label: 'Настройки', component: RecordSettings }] : [])];
  const appTab = appTabs.find((t) => t.key === tab);
  const texts = fields.filter((f) => f.fieldType === 'longtext');
  const relations = fields.filter((f) => f.fieldType === 'relation');
  const viewers = [...presence.entries()].filter(([uid, rid]) => rid === id && uid !== client.header?.userId).map(([uid]) => meta.user(uid)).filter(Boolean);

  return (
    <div className={cx(s.view, !embedded && s.page)}>
      <TopBar start={embedded ? <TopTitle as="span" icon={type?.icon ?? 'square'}>{type?.name}</TopTitle> : <RecordPath rec={rec} onBack={close} />}
        end={<>
          {viewers.length > 0 && <span className={s.viewers} title={`Сейчас смотрят: ${viewers.map((u) => u!.name).join(', ')}`}>{viewers.map((u) => <Avatar key={u!.id} name={u!.name} color={u!.color} src={avatarUrl(u)} size={20} />)}</span>}
          {embedded && <IconButton size="sm" icon="expand" label="Открыть страницей" onClick={embedded.onExpand} />}
          {embedded && <IconButton size="sm" icon="close" label="Закрыть" hint="Закрыть (Esc)" onClick={close} />}
        </>}
        more={<>
          <MenuItem icon="copy" onSelect={() => { void navigator.clipboard?.writeText(`${location.origin}/r/${id}`); toast({ kind: 'info', text: 'Ссылка скопирована' }); }}>Скопировать ссылку</MenuItem>
          <MenuItem icon="external" onSelect={() => window.open(`/r/${id}`, '_blank')}>Открыть в новой вкладке</MenuItem>
          <MenuSeparator />
          <MenuItem icon="archive" danger onSelect={() => setArchiving(true)}>В архив</MenuItem>
        </>} />
      <div className={s.scroll}>
        <div className={embedded ? s.inner : singleClass}>
          {titleField && <Title rec={rec} field={titleField} onDrop={close} level={embedded ? 2 : 1} />}
          <div className={s.meta}>
            <OwnerProp rec={rec} />
          </div>
          <RecordActions rec={rec} />
          {heads.map((h) => <section key={h.key} className={s.head} aria-label={h.label}><h2 className={s.headTitle}>{h.label}</h2><h.component recordId={id} /></section>)}
          {type?.key === 'email_template' && <div className={s.actions}><TemplateTestButton recordId={id} /></div>}
          <Tabs label="Разделы записи" value={tab} onChange={setTab} className={s.tabs} items={[
            ...appTabs.map((t) => ({ key: t.key, label: t.label })),
            { key: 'props', label: 'Свойства' },
            // One feed of events, revisions and versions (R-U22): no separate «Хронология» and «Ревизии».
            { key: 'history', label: 'История' },
            ...(rec.typeId === client.header?.scope?.typeId ? [{ key: 'activity', label: 'Журнал' }] : []),
            { key: 'auto', label: 'Автоматика' },
            // A type whose app registered its own «Письма» (the mail client's chains, R-M6) shows that one.
            ...(meta.typeByKey('email_message') && meta.typeByKey('email_message')!.id !== rec.typeId && !appTabs.some((t) => t.key === 'mail') ? [{ key: 'mail', label: 'Письма' }] : []),
          ]} />
          {tab === 'props' ? (
            <>
              <Properties rec={rec} fields={props} />
              {texts.map((f) => <LongText key={`${rec.id}:${f.id}`} rec={rec} field={f} onOpen={onOpen} />)}
              <RelationBlocks rec={rec} fields={relations} onOpen={onOpen} />
            </>
          ) : appTab ? <appTab.component recordId={id} /> : tab === 'history' ? <History recordId={id} /> : tab === 'activity' ? <ScopeActivity recordId={id} onOpen={onOpen} /> : tab === 'mail' ? <Letters recordId={id} onOpen={onOpen} /> : <RunLog recordId={id} />}
        </div>
      </div>
      {archiving && <ArchiveConfirm ids={[id]} title={`«${recordTitle(rec, meta)}»`} onClose={() => setArchiving(false)} onDone={onClose} />}
    </div>
  );
}

/** The record's title: the page's H1 on its own page, H2 in the reading column under a list (the list's head is the H1). */
function Title({ rec, field, onDrop, level = 1 }: { rec: RecordObj; field: FieldDef; onDrop?: () => void; level?: 1 | 2 }) {
  const Tag = level === 1 ? 'h1' : 'h2';
  const client = useSync();
  const writable = useWritable(rec);
  const meta = useMeta();
  const ref = useRef<HTMLHeadingElement>(null);
  const value = String(rec.data[field.key] ?? '');
  useEffect(() => { if (ref.current && document.activeElement !== ref.current) ref.current.textContent = value; }, [value]);
  // A fresh record opens with its title selected: typing replaces «Без названия» (L10).
  useEffect(() => {
    if (!freshRecords.has(rec.id) || !ref.current || !writable) return;
    ref.current.focus();
    const r = document.createRange(); r.selectNodeContents(ref.current);
    const sel = getSelection(); sel?.removeAllRanges(); sel?.addRange(r);
  }, [rec.id]);
  const commit = () => {
    const t = ref.current?.textContent?.trim() ?? '';
    if (!t) { if (ref.current) ref.current.textContent = value; return; }
    if (t !== value) setField(client, rec, field.key, t);
  };
  return (
    <Tag ref={ref} className={s.title} contentEditable={writable} suppressContentEditableWarning spellCheck={false} aria-label={field.name}
      data-placeholder={recordTitle(undefined, meta)}
      onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLElement).blur(); } if (e.key === 'Escape') { (e.target as HTMLElement).textContent = value; (e.target as HTMLElement).blur(); if (freshRecords.has(rec.id)) onDrop?.(); } }} />
  );
}

function OwnerProp({ rec }: { rec: RecordObj }) {
  const client = useSync();
  const writable = useWritable(rec);
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <button type="button" className={s.ownerBtn} disabled={!writable} onClick={() => setOpen(true)}>
          <span className={s.ownerLabel}>Ответственный</span>{rec.ownerId ? <UserValue id={rec.ownerId} /> : <span className={s.empty}>не назначен</span>}
        </button>
      </PopoverAnchor>
      <PopoverContent width={260}><UserPicker value={rec.ownerId} onDone={() => setOpen(false)} onChange={(v) => setOwner(client, rec, v)} /></PopoverContent>
    </Popover>
  );
}

export { RecordChip };
export { Prop, PropList, propsClass } from './Prop.tsx';
